/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { consoleCommands, events } from 'modules/common/ui-controller';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { trendActions } from '../../trend-tabs/slice';
import { TREND_FILTER_COMMAND_STEPS } from '../../trend-tabs/hooks/trend-command-steps';

// events of the trend tabular tab for the commands it shares with the trend tab
const TABULAR_EVENTS = {
  [consoleCommands.BOOKING_DATE]: events.SET_TREND_TABULAR_CUSTOM_BOOKING,
  [consoleCommands.CUMULATIVE_VIEW]: events.SET_TREND_TABULAR_CUMULATIVE_VIEW,
  [consoleCommands.SEGMENT]: events.SET_TABULAR_SEGMENT_BREAKDOWN,
  [consoleCommands.MOVING_AVERAGE]: events.SET_TREND_TABULAR_MOVING_AVERAGE,
  [consoleCommands.COMPARE_HOTEL]: events.SET_TREND_TABULAR_HOTELS_COMPARE_HOTELS,
  [consoleCommands.COMPARE_HOTEL_GROUP]: events.SET_TREND_TABULAR_HOTEL_GROUP,
  [consoleCommands.ALIGNMENT]: events.TREND_TABULAR_TIME_ALIGNMENT,
  [consoleCommands.SYMMETRIC]: events.SET_TREND_TABULAR_SYMMETRIC_COMPARISON,
  [consoleCommands.BREAKDOWN_TOGGLE]: events.SET_TREND_TABULAR_BREAKDOWN_TOGGLE,
  [consoleCommands.COMPARE_WINDOW]: events.SET_TREND_TABULAR_COMPARE_WINDOW,
  [consoleCommands.CUSTOM_DATES]: events.SET_TREND_TABULAR_CUSTOM_DATE_RANGE,
  [consoleCommands.WEEKLY_VIEW]: events.SET_TREND_TABULAR_WEEKLY_VIEW,
  [consoleCommands.PERCENTAGE_TOGGLE]: events.SET_TREND_TABULAR_PERCENTAGE_TOGGLE,
  [consoleCommands.SEGMENT_BREAKDOWN]: events.SET_TREND_TABULAR_SEGMENT_BREAKDOWN_TOGGLE,
  [consoleCommands.FOCUS]: events.SET_TREND_TABULAR_SEGMENT_FOCUS_ON,
  [consoleCommands.SET_A]: events.SET_TREND_TABULAR_CUSTOM_AGGREGATION_A_VALUE,
  [consoleCommands.SET_B]: events.SET_TREND_TABULAR_CUSTOM_AGGREGATION_B_VALUE,
  [consoleCommands.FORECAST]: events.SET_TREND_TABULAR_FORECAST,
  [consoleCommands.NORMALIZED_VIEW]: events.SET_TREND_TABULAR_HOTEL_NORMALIZED_VIEW,
};
// stay date, cumulative view and weekly view move the trend date range, and the granularity
// filter goes back to its default each time the range moves. this wait lets the range settle so
// the granularity command is not overwritten by that default
const GRANULARITY_DELAY = 2000;
// trend tabular tab has the granularity command, applied after the weekly view command
const GRANULARITY_STEP = {
  command: consoleCommands.GRANULARITY,
  event: events.SET_TREND_TABULAR_GRANULARITY,
  ready: (context) => context.isLoaded,
  delayBefore: GRANULARITY_DELAY,
  apply: (value, { dispatch }) => {
    dispatch(trendActions.setTrendGranularity(value || 'Default'));
  },
};
/**
 * Trend tabular tab filter commands in the order they are applied.
 * Same filter steps as the trend tab (TREND_FILTER_COMMAND_STEPS), listening to the trend tabular
 * events, followed by the pms sync, vi response and symmetric comparison collapse commands.
 * The position in this list is the execution order, see useCommandPipeline.
 */
const TREND_TABULAR_FILTER_COMMAND_STEPS = TREND_FILTER_COMMAND_STEPS.filter(
  // trend tabular tab has no tabular view command
  (step) => step.command !== consoleCommands.TABULAR_VIEW
).flatMap((step) => {
  const event = TABULAR_EVENTS[step.command];
  if (!event) {
    throw new Error(`trend tabular event is not defined for the "${step.command}" command`);
  }
  const tabularStep = { ...step, event };
  return step.command === consoleCommands.WEEKLY_VIEW
    ? [tabularStep, GRANULARITY_STEP]
    : [tabularStep];
});

// time the symmetric comparison visual stays hidden before the command value is applied
const SYMMETRIC_COMPARISON_COLLAPSE_DELAY = 3000;
// symmetric comparison visual is hidden first, then displayed according to the command value
const SYMMETRIC_COMPARISON_COLLAPSE_STEP = {
  command: consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE,
  event: events.SET_TREND_TABULAR_SYMMETRIC_COMPARISON_COLLAPSE,
  ready: (context) => context.isLoaded,
  start: (value, { dispatch }) => {
    dispatch(trendActions.setSymmetricComparisonIsDisplay(false));
  },
  delayBefore: SYMMETRIC_COMPARISON_COLLAPSE_DELAY,
  apply: (value, { dispatch }) => {
    dispatch(trendActions.setSymmetricComparisonIsDisplay(value === 1));
  },
};
const TREND_TABULAR_COMMAND_STEPS = [
  ...TREND_TABULAR_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  SYMMETRIC_COMPARISON_COLLAPSE_STEP,
];

export default TREND_TABULAR_COMMAND_STEPS;
